import { ComponentType } from "react";
import { classNames } from "./Utils/classNames.ts";
import { PrimaryLoadingIndicator } from "./Loading/PrimaryLoadingIndicator.tsx";
import { LoaderSize } from "./Loading/LoaderSize.tsx";

type Props = {
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
  hasAlert?: boolean;
  tone?: "neutral" | "danger";
  disabled?: boolean;
  disabledReason?: string;
  loading?: boolean;
};

const classNamesByTone = {
  neutral: "border-gray-300 text-gray-700 hover:bg-gray-50",
  danger: "border-red-200 text-red-600 hover:bg-red-50",
};

export function IconButton({
  label,
  icon: Icon,
  onClick,
  hasAlert,
  tone = "neutral",
  disabled = false,
  disabledReason,
  loading = false,
}: Props) {
  const isInactive = disabled || loading;
  const tooltip =
    disabled && disabledReason ? `${label}: ${disabledReason}` : label;
  return (
    <div className="relative group shrink-0">
      <button
        type="button"
        onClick={() => {
          if (!isInactive) onClick();
        }}
        aria-label={label}
        aria-disabled={isInactive}
        className={classNames(
          "p-[9px] rounded-lg border bg-white",
          classNamesByTone[tone],
          disabled ? "opacity-50 cursor-not-allowed" : undefined,
          loading ? "cursor-wait" : undefined,
        )}
      >
        {loading ? (
          <PrimaryLoadingIndicator size={LoaderSize.Small} />
        ) : (
          <Icon className="w-5 h-5" />
        )}
      </button>
      {hasAlert && (
        <span className="pointer-events-none absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
      )}
      <span
        role="tooltip"
        className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 z-20 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {tooltip}
      </span>
    </div>
  );
}
